import React from "react";
import { useMediaQuery } from "react-responsive";
import v2 from "../../styles/mainv2.module.css";

/**
 * 담임멘토 관리진 — claude.ai/design `메인 통합본.html` 05 섹션의 관리진 블록.
 * 메인(/)과 담임멘토 소개(/teachers) 양쪽에서 사용.
 * 섹션 래퍼(.mtMentors)는 페이지가 소유하고, 이 컴포넌트는 .mtWrap 블록만 렌더한다.
 */

// 사진은 R2 CDN에 한글 파일명으로 업로드되어 있음.
const R2_TEACHERS = "https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/homepage/teachers/WEBP";
export const mentorPhoto = (name: string) => `${R2_TEACHERS}/${encodeURIComponent(name)}.webp`;

const mentorLeads: Array<{ name: string; photoName?: string; role: string; badge: string; badgeColor: string; photoTop: number; career: string[]; careerMobile: string[] }> = [
    {
        name: "이종호", role: "멘토 총괄 팀장", badge: "성균관대학교 약학대 졸업", badgeColor: "#bcd43f", photoTop: -13,
        career: ["2016년 한양대 화학공학과 수시 입학", "2022년 성균관대 약학과 입학", "(약학대학입문자격시험(PEET) 표준점수", "상위 0.8%, 경희대 약학과 동시합격)", "2026년 성균관대 약학과 졸업"],
        careerMobile: ["한양대 화학공학과 입학", "성균관대 약학과 입학", "(PEET 표준점수 상위 0.8%, 경희대 약학과 동시합격)", "성균관대 약학과 졸업"],
    },
    {
        // R2 파일명은 "김고은.webp" (표기 이름과 다름 — 디자인 원본 기준)
        name: "김고운", photoName: "김고은", role: "멘토 팀장", badge: "성균관대학교 약학대 졸업", badgeColor: "#bcd43f", photoTop: -11,
        career: ["2021년 서울대 지구과학교육과 수시 입학", "(카이스트 단일계열, 포항공대 단일계열 동시합격)", "2022년 성균관대 약학과 입학", "(숙명여대 약학과 동시합격)", "2026년 성균관대 약학과 졸업"],
        careerMobile: ["서울대 지구과학교육과 입학", "(카이스트·포항공대 단일계열 동시합격)", "성균관대 약학과 입학", "(숙명여대 약학과 동시합격)", "성균관대 약학과 졸업"],
    },
    {
        name: "김지원", role: "멘토 매니저", badge: "이화여자대학교 약학대", badgeColor: "#006e38", photoTop: -12,
        career: ["2022년 성균관대 경제학과 졸업", "2023년 이화여대 약학과 정시 전액장학 합격", "(상지대 한의예과 및 대전대 한의예과 정시 동시 합격)"],
        careerMobile: ["성균관대 경제학과 졸업", "이화여대 약학과 전액장학 합격", "(상지대·대전대 한의예과 동시 합격)"],
    },
    {
        name: "김동주", role: "멘토 매니저", badge: "이화여자대학교 약학대", badgeColor: "#006e38", photoTop: -29,
        career: ["2022년 고려대 건축사회환경공학부 수시 합격", "2023년 이화여대 약학과 정시 합격", "(상지대학교 한의예과 정시 동시 합격)"],
        careerMobile: ["고려대 건축사회환경공학부 합격", "이화여대 약학과 합격", "(상지대학교 한의예과 동시 합격)"],
    },
    {
        name: "박상혁", role: "멘토 매니저", badge: "한양대학교 의예과", badgeColor: "#004483", photoTop: -13,
        career: ["2023년 한양대 의예과 입학", "(경희대 의예과, 순천향대 의예과,", "건국대 수의예과 동시합격)", "수능선배 담임멘토 2년 근무", "대치동 학원 학생들 멘토링 섭외 1순위"],
        careerMobile: ["2023년 한양대 의예과 입학", "(경희대 의예과, 순천향대 의예과,", "건국대 수의예과 동시합격)", "수능선배 담임멘토 2년 근무", "대치동 학원 학생들 멘토링 섭외 1순위"],
    },
];

const MentorLeads: React.FC = () => {
    const isMobile = useMediaQuery({ query: "(max-width : 768px)" });

    return (
        <div className={v2.mtWrap}>
            <h2 className={v2.mtTitle}>담임멘토 관리진</h2>
            <p className={v2.mtDesc}>
                수능선배의 담임멘토는 검증된 관리진이 직접 선발하고 교육합니다.<br />
                약대·명문대 출신 관리진이 멘토링 커리큘럼 설계부터 학생 관리 품질까지 책임지고 있습니다.
            </p>
            <div className={v2.mtGrid}>
                {[mentorLeads.slice(0, 2), mentorLeads.slice(2, 4), mentorLeads.slice(4)].map((row, ri) => (
                    <div className={v2.mtRow} key={`leadRow${ri}`}>
                        {row.map((m) => (
                            <div className={v2.mtCardWide} key={m.name}>
                                <span className={v2.mtBadge} style={{ background: m.badgeColor }}>{m.badge}</span>
                                <div className={v2.mtInfo}>
                                    <div className={v2.mtName}>{m.name}<br />{m.role}</div>
                                    <div className={v2.mtCareer}>
                                        {(isMobile ? m.careerMobile : m.career).map((line, li, arr) => (
                                            <React.Fragment key={li}>
                                                {line}
                                                {li < arr.length - 1 && <br />}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                                <img className={v2.mtPhoto} style={{ top: m.photoTop }} src={mentorPhoto(m.photoName ?? m.name)} alt={`${m.name} ${m.role}`} />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MentorLeads;
